import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { useFavoritesEnabled } from '../../../../utils/useFavoritesEnabled';
import { IconShare } from '../../../icon/IconShare';
import { IconButton } from '../../../input/IconButton';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { PostUI, ReactionState } from '../../types';
import { isPostStatusValidForReaction } from '../../utils/isPostStatusValidForReaction';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import {
    BookmarkFilledIcon,
    BookmarkIcon,
    DislikeFilledIcon,
    DislikeIcon,
    DislikeThumbFilledIcon,
    DislikeThumbIcon,
    FavoriteHeartIcon,
    LikeThumbFilledIcon,
    LikeThumbIcon,
    PostControlsContainer,
} from '../styles';
import { makeMediaParams } from '../utils';
import { isPostStatusValidForSave } from './utils/isPostStatusValidForSave';

const ICON_COLOR = 'var(--always-white)';

interface PostControlsProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
}

export const PostControls: FC<PostControlsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    onShare,
}) => {
    const { activeIndex } = usePostDetailsCardContext();
    const isFavoritesEnabled = useFavoritesEnabled();
    const isLiked = post.reactionState === 'like';
    const canReact = isPostStatusValidForReaction(post.status);

    const handleBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onBookmark?.();
            event.stopPropagation();
        },
        [onBookmark],
    );

    const handleReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            onReaction({ postNumber: index, reactionState });
        },
        [index, onReaction],
    );

    const handleShare = useCallback(
        () => onShare?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [post.content, activeIndex, onShare],
    );

    const { likeIcon, dislikeIcon, bookmarkIcon } = useMemo(() => {
        const isDisliked = post.reactionState === 'dislike';
        const isSaved = Boolean(post.saved);

        let like;
        if (isFavoritesEnabled) {
            like = isLiked ? (
                <LikeThumbFilledIcon color={ICON_COLOR} />
            ) : (
                <LikeThumbIcon color={ICON_COLOR} />
            );
        } else {
            like = <FavoriteHeartIcon color={ICON_COLOR} isActive={isLiked} />;
        }

        let dislike;
        if (isFavoritesEnabled) {
            dislike = isDisliked ? (
                <DislikeThumbFilledIcon color={ICON_COLOR} />
            ) : (
                <DislikeThumbIcon color={ICON_COLOR} />
            );
        } else {
            dislike = isDisliked ? (
                <DislikeFilledIcon color={ICON_COLOR} />
            ) : (
                <DislikeIcon color={ICON_COLOR} />
            );
        }

        let bookmark;
        if (isFavoritesEnabled) {
            bookmark = <FavoriteHeartIcon color={ICON_COLOR} isActive={isSaved} />;
        } else {
            bookmark = isSaved ? (
                <BookmarkFilledIcon color={ICON_COLOR} />
            ) : (
                <BookmarkIcon color={ICON_COLOR} />
            );
        }

        return { likeIcon: like, dislikeIcon: dislike, bookmarkIcon: bookmark };
    }, [isFavoritesEnabled, isLiked, post.reactionState, post.saved]);

    // 100% — ширина поста: контейнер лежит в Box с width={sizes.width}
    return (
        <PostControlsContainer bottom={1.5} left="calc(100% - 52px)" maxWidth={{ sm: '100%' }}>
            {canReact ? (
                <IconButton
                    dataTestId={DataTestId.LIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.LIKE);
                    }}
                >
                    {likeIcon}
                </IconButton>
            ) : null}

            {canReact && post.business ? (
                <IconButton
                    dataTestId={DataTestId.DISLIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.DISLIKE);
                    }}
                >
                    {dislikeIcon}
                </IconButton>
            ) : null}

            {isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner && (
                <IconButton onClick={handleBookmark} dataTestId={DataTestId.BOOKMARK}>
                    {bookmarkIcon}
                </IconButton>
            )}

            <IconButton onClick={handleShare} dataTestId={DataTestId.SHARE}>
                <IconShare color={ICON_COLOR} />
            </IconButton>
        </PostControlsContainer>
    );
};
